import { useIntl } from 'react-intl';

import type { Notification, NotificationCounts } from '@hooks/notifications';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  List,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';

import { NotificationRow } from './NotificationRow.js';
import type { NotificationsTab } from './notifications.utils.js';

interface NotificationsPanelProps {
  counts: NotificationCounts;
  hasError: boolean;
  hasNextPage: boolean;
  isFetching: boolean;
  isFetchingNextPage: boolean;
  notifications: Notification[];
  sentinelRef: (node: Element | null) => void;
  tab: NotificationsTab;
  onMarkAllAsRead: () => void;
  onNotificationClick: (notification: Notification) => void;
  onRetry: () => void;
  onTabChange: (tab: NotificationsTab) => void;
}

export function NotificationsPanel({
  counts,
  hasError,
  hasNextPage,
  isFetching,
  isFetchingNextPage,
  notifications,
  sentinelRef,
  tab,
  onMarkAllAsRead,
  onNotificationClick,
  onRetry,
  onTabChange,
}: NotificationsPanelProps) {
  const intl = useIntl();
  const now = Date.now();
  const isEmpty = notifications.length === 0;
  const showSpinner = !hasError && (isFetchingNextPage || (isEmpty && isFetching));

  return (
    <Stack sx={{ maxHeight: { xs: '70dvh', sm: 560 } }}>
      <Stack
        alignItems="center"
        direction="row"
        justifyContent="space-between"
        spacing={1}
        sx={{ px: 2, pt: 2, pb: 1 }}
      >
        <Typography variant="h6">
          {intl.formatMessage({ id: 'NOTIFICATIONS.TITLE', defaultMessage: 'Notifications' })}
        </Typography>
        <Button disabled={counts.unread === 0} size="small" onClick={onMarkAllAsRead}>
          {intl.formatMessage({
            id: 'NOTIFICATIONS.MARK_ALL_AS_READ',
            defaultMessage: 'Mark all as read',
          })}
        </Button>
      </Stack>
      <Tabs
        sx={{ px: 2 }}
        value={tab}
        onChange={(_event, value: NotificationsTab) => onTabChange(value)}
      >
        <Tab
          label={intl.formatMessage(
            { id: 'NOTIFICATIONS.TAB.ALL', defaultMessage: 'All ({count})' },
            { count: counts.total }
          )}
          value="all"
        />
        <Tab
          label={intl.formatMessage(
            { id: 'NOTIFICATIONS.TAB.UNREAD', defaultMessage: 'Unread ({count})' },
            { count: counts.unread }
          )}
          value="unread"
        />
      </Tabs>
      <Divider />
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {notifications.length > 0 && (
          <List disablePadding>
            {notifications.map((notification) => (
              <NotificationRow
                key={notification.id}
                notification={notification}
                now={now}
                onClick={onNotificationClick}
              />
            ))}
          </List>
        )}
        {hasNextPage && !hasError && <Box ref={sentinelRef} sx={{ height: 1 }} />}
        {showSpinner && (
          <Stack alignItems="center" sx={{ py: isEmpty ? 4 : 2 }}>
            <CircularProgress size={24} />
          </Stack>
        )}
        {hasError && !isFetching && (
          <Stack
            alignItems="center"
            spacing={1}
            sx={{ px: 3, py: isEmpty ? 5 : 2, textAlign: 'center' }}
          >
            <Typography color="text.secondary" variant="body2">
              {intl.formatMessage({
                id: 'NOTIFICATIONS.ERROR',
                defaultMessage: 'The notifications could not be loaded.',
              })}
            </Typography>
            <Button size="small" onClick={onRetry}>
              {intl.formatMessage({ id: 'NOTIFICATIONS.RETRY', defaultMessage: 'Retry' })}
            </Button>
          </Stack>
        )}
        {isEmpty && !isFetching && !hasError && (
          <Stack alignItems="center" spacing={1} sx={{ px: 3, py: 5, textAlign: 'center' }}>
            <NotificationsNoneOutlinedIcon color="disabled" sx={{ fontSize: 40 }} />
            <Typography variant="subtitle1">
              {intl.formatMessage({
                id: 'NOTIFICATIONS.EMPTY.TITLE',
                defaultMessage: 'No notifications',
              })}
            </Typography>
            <Typography color="text.secondary" variant="body2">
              {intl.formatMessage({
                id: 'NOTIFICATIONS.EMPTY.DESCRIPTION',
                defaultMessage:
                  'When an email, import, merge, duplication or refund you start finishes, the result appears here.',
              })}
            </Typography>
          </Stack>
        )}
      </Box>
    </Stack>
  );
}
